//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { deserializeMediaItems } from '@apis/media-library/mediaLibraryApiUtils';
import { deserializeConnectionArray } from '@apis/universal-posts/connections/universalPostConnectionUtils';
import { searchDraftPosts } from '@apis/universal-posts/draft-posts/universalDraftPostsApi';
import { IDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import { UniversalPostingGroupName } from '@apis/universal-posts/universalPostsApiModel';
import { mapPostConfigToPostType } from '@apis/universal-posts/universalPostsUtils';
import { isDefined } from '@utils/filterPredicates';
import { Serialized } from '@utils/types/Serialized';

export const deserializeDraftPost = (
  draftPost: Serialized<IDraftPost>,
): IDraftPost => ({
  ...draftPost,
  postType: mapPostConfigToPostType(draftPost.config['@type']),
  createdAt: new Date(draftPost.createdAt),
  scheduledTime: isDefined(draftPost.scheduledTime)
    ? new Date(draftPost.scheduledTime)
    : undefined,
  mediaItems: deserializeMediaItems(draftPost.mediaItems),
  connections: deserializeConnectionArray(draftPost.connections),
});

export const getAllInstagramDraftPosts = (): Promise<IDraftPost[]> => {
  const LIMIT = 25;

  const getRemainingItems = async (
    acc: IDraftPost[] = [],
    offset = 0,
  ): Promise<IDraftPost[]> => {
    const { data: result } = await searchDraftPosts({
      groups: [
        UniversalPostingGroupName.InstagramFeedAutomaticGroup,
        UniversalPostingGroupName.InstagramFeedManualGroup,
        UniversalPostingGroupName.InstagramReelsAutomaticGroup,
        UniversalPostingGroupName.InstagramReelsManualGroup,
        UniversalPostingGroupName.InstagramStoryGroupAutomaticPostingGroup,
        UniversalPostingGroupName.InstagramStoryGroupPostingGroup,
      ],
      limit: LIMIT,
      offset,
    });

    const concatenatedResults = acc.concat(result);

    if (result.length !== LIMIT) {
      return concatenatedResults;
    }

    return getRemainingItems(concatenatedResults, offset + LIMIT);
  };

  return getRemainingItems();
};
